import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Avatar } from "./kit";
import { label } from "../lib/api";
import { toast } from "sonner";
import { errMsg } from "../lib/api";

const BIG = "min-h-[60px] rounded-xl text-base font-extrabold uppercase tracking-wide";

export const ACTIONS = [
  ["yellow", "Yellow Card", "bg-[#FFD400] text-[#17191C] hover:bg-[#f0c800]"],
  ["red", "Red Card", "bg-[#F02030] text-white hover:bg-[#d81a29]"],
  ["foul", "Foul", "bg-[#343A40] text-white hover:bg-[#17191C]"],
  ["goal", "Goal", "bg-[#17191C] text-white hover:bg-black"],
  ["own_goal", "Own Goal", "bg-[#E6E9ED] text-[#17191C] hover:bg-[#C5CBD1]"],
  ["substitution", "Substitution", "bg-[#E6E9ED] text-[#17191C] hover:bg-[#C5CBD1]"],
  ["note", "Incident Note", "bg-white border border-[#C5CBD1] text-[#17191C] hover:bg-[#F7F8F9]"],
];

export function PlayerActionDialog({ player, club, bench, onClose, onConfirm, allowed = ACTIONS.map((a) => a[0]) }) {
  const [step, setStep] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const close = () => { setStep(null); setNote(""); onClose(); };
  const go = async (type, extra = {}) => {
    setBusy(true);
    try {
      await onConfirm(type, { notes: note || undefined, ...extra });
      close();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const pick = (t) => (["yellow", "red", "goal", "own_goal"].includes(t) ? setStep(t) : t === "substitution" ? setStep("sub") : t === "note" ? setStep("note") : go(t));
  return (
    <Dialog open={!!player} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="player-action-menu">
        {player && <>
          <DialogHeader><DialogTitle className="sr-only">Player actions</DialogTitle></DialogHeader>
          <div className="flex items-center gap-4 rounded-xl tro-metal p-3" data-testid="action-player-info">
            <Avatar player={player} size={64} />
            <div>
              <div className="text-xl font-extrabold">#{player.number} {player.first_name} {player.last_name}</div>
              <div className="text-sm font-semibold text-[#343A40]">{club} · {player.position}</div>
            </div>
          </div>
          {!step && <div className="grid grid-cols-2 gap-2">
            {ACTIONS.filter((a) => allowed.includes(a[0])).map(([k, l, c]) => (
              <button key={k} data-testid={`action-${k}`} onClick={() => pick(k)} className={`${BIG} ${c} transition-colors ${k === "note" ? "col-span-2" : ""}`}>{l}</button>
            ))}
          </div>}
          {["yellow", "red", "goal", "own_goal"].includes(step) && (
            <div className="space-y-3">
              <div className={`rounded-xl p-4 text-center text-lg font-extrabold uppercase ${step === "yellow" ? "bg-[#FFD400]" : step === "red" ? "bg-[#F02030] text-white" : "bg-[#17191C] text-white"}`}>Confirm {label(step)}</div>
              <Textarea data-testid="action-note-input" placeholder="Optional note (reason, description)" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className={BIG} onClick={() => setStep(null)}>Back</Button>
                <Button data-testid="confirm-action-btn" disabled={busy} className={BIG} onClick={() => go(step)}>Confirm</Button>
              </div>
            </div>
          )}
          {step === "sub" && (
            <div className="space-y-2">
              <div className="tro-label">Select incoming substitute</div>
              {!bench?.length && <div className="text-sm text-[#6C757D]">No substitutes available.</div>}
              <div className="grid max-h-72 grid-cols-2 gap-2 overflow-auto">
                {bench?.map((b) => (
                  <button key={b.id} data-testid={`sub-in-${b.number}`} disabled={busy} onClick={() => go("substitution", { player_in_id: b.id })} className="flex items-center gap-2 rounded-lg border border-[#E6E9ED] p-2 text-left hover:bg-[#F7F8F9]">
                    <Avatar player={b} size={36} /><span className="text-sm font-bold">#{b.number} {b.short_name}</span>
                  </button>
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={() => setStep(null)}>Back</Button>
            </div>
          )}
          {step === "note" && (
            <div className="space-y-2">
              <Textarea data-testid="incident-note-input" placeholder="Incident note" value={note} onChange={(e) => setNote(e.target.value)} />
              <Button data-testid="save-note-btn" disabled={!note || busy} className={`${BIG} w-full`} onClick={() => go("note")}>Save note</Button>
            </div>
          )}
        </>}
      </DialogContent>
    </Dialog>
  );
}

const CLOCK = {
  pre: [["start", "Start First Half"], ["delay", "Delay Kickoff"]],
  playing: [["pause", "Pause Clock"], ["resume", "Resume Clock"], ["end_period", "End Period"], ["suspend", "Suspend Match"], ["abandon", "Abandon Match"]],
  break: [["start", "Start Next Period"]],
  suspended: [["resume_match", "Resume Match"], ["abandon", "Abandon Match"]],
};
const PRE = ["scheduled", "awaiting_team_sheets", "teams_submitted", "teams_approved", "officials_confirmed", "ready_for_kickoff", "delayed"];

export function ClockDialog({ open, onClose, match, onAction }) {
  const [mins, setMins] = useState(2);
  const [next, setNext] = useState("full_time");
  if (!match) return null;
  const s = match.status;
  const group = PRE.includes(s) ? "pre" : ["half_time", "et_break", "et_half_time", "pen_break"].includes(s) ? "break" : s === "suspended" ? "suspended" : ["first_half", "second_half", "et_first_half", "et_second_half", "penalty_shootout"].includes(s) ? "playing" : null;
  const run = async (a, extra = {}) => {
    try {
      await onAction(a, extra);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="clock-dialog">
        <DialogHeader><DialogTitle>Match Time · {label(s)}</DialogTitle></DialogHeader>
        {!group && <div className="text-sm text-[#6C757D]">Match clock is closed for this status.</div>}
        <div className="grid gap-2">
          {(CLOCK[group] || []).map(([a, l]) => (
            <button key={a} data-testid={`clock-${a}`} onClick={() => run(a, a === "end_period" ? { next } : {})}
              className={`${BIG} ${a === "abandon" || a === "suspend" ? "bg-[#F02030] text-white" : a === "start" || a === "resume_match" ? "bg-[#1E7E34] text-white" : "bg-[#17191C] text-white"}`}>{l}</button>
          ))}
        </div>
        {(s === "second_half" || s === "et_second_half") && (
          <div className="flex items-center gap-2 text-sm"><span className="tro-label">After this period</span>
            <select data-testid="clock-next-select" value={next} onChange={(e) => setNext(e.target.value)} className="h-9 flex-1 rounded-md border px-2">
              <option value="full_time">Full time</option>{s === "second_half" && <option value="extra_time">Extra time</option>}<option value="penalties">Penalty shootout</option>
            </select></div>
        )}
        {group === "playing" && (
          <div className="flex items-center gap-2 border-t pt-3">
            <span className="tro-label">Added time</span>
            <Input data-testid="added-time-input" type="number" min={0} max={30} value={mins} onChange={(e) => setMins(+e.target.value)} className="w-20" />
            <Button data-testid="clock-add-time" onClick={() => run("add_time", { minutes: mins })}>Set +{mins}'</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TextDialog({ open, title, placeholder, onClose, onSubmit, testid = "text-dialog", extra }) {
  const [v, setV] = useState("");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {extra}
        <Textarea data-testid={`${testid}-input`} placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} />
        <Button data-testid={`${testid}-submit`} disabled={!v.trim()} className={BIG} onClick={async () => {
          try { await onSubmit(v); setV(""); onClose(); } catch (e) { toast.error(errMsg(e)); }
        }}>Submit</Button>
      </DialogContent>
    </Dialog>
  );
}

export function PlayerPicker({ open, title, teams, onClose, onPick }) {
  const [team, setTeam] = useState(0);
  const t = teams?.[team];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="player-picker">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="flex gap-2">{teams?.map((x, i) => (
          <button key={x.id} data-testid={`picker-team-${i}`} onClick={() => setTeam(i)} className={`flex-1 rounded-lg px-3 py-3 text-sm font-extrabold ${team === i ? "bg-[#17191C] text-white" : "bg-[#F7F8F9]"}`}>{x.name}</button>
        ))}</div>
        <div className="grid max-h-[50vh] grid-cols-2 gap-2 overflow-auto sm:grid-cols-3">
          {t?.players.map((p) => (
            <button key={p.id} data-testid={`picker-player-${p.number}`} onClick={() => onPick(p, t)} className="flex items-center gap-2 rounded-lg border border-[#E6E9ED] p-2 text-left hover:bg-[#F7F8F9]">
              <Avatar player={p} size={40} /><div><div className="text-sm font-bold">#{p.number} {p.short_name}</div><div className="text-[10px] text-[#6C757D]">{p.position}</div></div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
